
import {defineComponent} from 'vue'
import {UIStateDto} from "@/dtos/UIStateDto";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {useToast} from "vue-toastification";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {
  AsyncHandlingCriteriaDto,
  AsyncHandlingOverviewRestDto,
  PagedAsyncHandlingOverviewRestDto
} from "@/api/pharma-master-async-handling/models";
import {
  PagedAsyncHandlingOverviewRestDtoModel
} from "@/models/api/pharma-master/PagedAsyncHandlingOverviewRestDtoModel";
import {AsyncHandlingRestService} from "@/services/rest/async-handling/AsyncHandlingRestService";
import BaseModal from "@/components/UI/Bootstrap/Modal/BaseModal.vue";
import BaseModalButton from "@/components/UI/Bootstrap/Modal/BaseModalButton.vue";
import BasePagination from "@/components/UI/Bootstrap/BasePagination.vue";

export default defineComponent({
  name: "AsyncHandlingOverview",
  components: {
    BasePagination,
    BaseContainer,
    BaseTitle,
    AlertError2,
    BaseModal,
    BaseModalButton,
  },
  props: {
    moduleCode: {
      type: String,
      required: true
    },
    pollingId: {
      type: String,
      required: false,
      default: null
    },
    backRoute: {
      type: String,
      required: true
    }
  },
  computed: {
    getAsyncHandlings(): AsyncHandlingOverviewRestDto[] {
      if (!this.asyncHandlings.results?.length) {
        return [];
      }

      return this.asyncHandlings.results!;
    },
  },
  data: () => ({
    asyncHandlings: PagedAsyncHandlingOverviewRestDtoModel.createWithDefaults() as PagedAsyncHandlingOverviewRestDto,
    toaster: useToast(),
    pageUI: UIStateDto.createWithDefaults(),
    refreshInterval: null as number | null,
  }),
  mounted() {
    this.reloadContent();
    this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    getStatusClass(statusCode: string): string {
      const baseClasses = ['status-badge'];

      if (['QUEUED', 'BUSY'].includes(statusCode)) {
        baseClasses.push('status-processing');
        if (this.shouldAutoRefresh()) {
          baseClasses.push('status-glow');
        }
      } else if (statusCode === 'SUCCESSFUL') {
        baseClasses.push('status-success');
      } else if (['FAILED', 'INTERRUPTED'].includes(statusCode)) {
        baseClasses.push('status-error');
      }

      return baseClasses.join(' ');
    },
    startAutoRefresh(): void {
      // Clear any existing interval first
      this.stopAutoRefresh();

      if (this.shouldAutoRefresh()) {
        // Set up the new interval to refresh every 5 seconds
        this.refreshInterval = setInterval(() => {
          if (this.shouldAutoRefresh()) {
            this.reloadContent();
          } else {
            this.stopAutoRefresh();
          }
        }, 5000);
      }
    },
    stopAutoRefresh(): void {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async reloadContent(): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        const criteria: AsyncHandlingCriteriaDto = {
          x_dp_module_code: this.moduleCode,
          search_description: null,
          filter_polling_id: this.pollingId || null,
          filter_creation_date_from: null,
          filter_creation_date_till: null,
          filter_qt_type_code: null,
          filter_qte_status_code: null,
          page: this.asyncHandlings.paging_metadata.page_number,
          page_size: this.asyncHandlings.paging_metadata.size
        }

        this.asyncHandlings = await AsyncHandlingRestService.getInstance().findAsyncHandlings(criteria);
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    shouldAutoRefresh(): boolean {
      if (!this.pollingId) {
        return false;
      }
      if (!this.asyncHandlings.results?.length) {
        return true;
      }
      return ['QUEUED', 'BUSY'].includes(this.asyncHandlings.results[0].status.code);
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    hasFullResultInfo(asyncHandling: AsyncHandlingOverviewRestDto): boolean {
      return !!asyncHandling.full_result_info &&
             !!asyncHandling.short_result_info &&
             asyncHandling.full_result_info !== asyncHandling.short_result_info;
    },
    onChangePageNumber(number: number): void {
      this.asyncHandlings.paging_metadata.page_number = number;
      this.reloadContent();
    },
  }
})
